import obzerv from 'lib/intersection';
import {
  formatPriceString,
  hasTag,
  getValueForTag,
  formatPrice,
  getAllValueForTag,
  htmlDecode,
  convertGQLToProductJson,
} from 'utils/general';
import storefront from 'lib/storefront';
import * as gqlQueries from 'lib/graphql/queries';

const CHAT_IFRAME_TITLE_SELECTOR = 'Button to launch messaging window';

function getCategory(product) {
  return `${product.type || product.product_type || 'Unspecified'}`;
}

function getProductTitle(product) {
  const title = new DOMParser().parseFromString(
    (product.title?.split(window.__GLOBAL__.productDelimiter)[0] || '').trim(),
    'text/html',
  ).documentElement.textContent;

  return title.trim();
}

function getVariantSize(variant) {
  return variant.title.toLocaleLowerCase().includes('mini')
    ? 'Mini Size'
    : 'Full Size';
}

function formatString(str) {
  return str.replace(/[^A-Za-z0-9]+/g, '-').toLowerCase();
}

export function setLocalStorageProperties(otherData, listData) {
  window.localStorage.setItem('tt_origin', otherData.origin);
  window.localStorage.setItem('tt_origin_product', otherData.originProduct);
  window.localStorage.setItem('tt_list_id', listData.listId);
  window.localStorage.setItem('tt_list_name', listData.listName);
  window.localStorage.setItem('tt_list_index', listData.index);
}

export function getLocalStorageProperties() {
  return {
    ...(window.localStorage.getItem('tt_origin') && {
      _origin: window.localStorage.getItem('tt_origin'),
    }),
    ...(window.localStorage.getItem('tt_origin_product') && {
      _origin_product: window.localStorage.getItem('tt_origin_product'),
    }),
    ...(window.localStorage.getItem('tt_list_id') && {
      _item_list_id: window.localStorage.getItem('tt_list_id'),
    }),
    ...(window.localStorage.getItem('tt_list_name') && {
      _item_list_name: window.localStorage.getItem('tt_list_name'),
    }),
    ...(window.localStorage.getItem('tt_list_index') && {
      _index: parseInt(window.localStorage.getItem('tt_list_index')),
    }),
  };
}

export function addToCartEventData(product, quantity, otherData) {
  if (!product.id) {
    return false;
  }

  let productType = 'Regular';
  if (hasTag('sample', product.tags)) {
    productType = 'Sample';
  } else if (hasTag('gift', product.tags)) {
    productType = 'Gift';
  }

  if (hasTag('bundle', product.tags)) {
    product.isBundle = true;
  }

  const productContext = getAllValueForTag('badge', product.tags);
  const productMasterId = getValueForTag('master-id', product.tags);

  const price = Object.hasOwn(otherData, 'subscriptionPrice')
    ? otherData.subscriptionPrice
    : product.price
    ? product.price
    : Number(product.variants[0].price);

  const eventData = {
    addToCartType: otherData.addToCartType,
    isDonation: false,
    products: [
      {
        brand: product.vendor === 'Fenty Skin' ? 'Fenty Skin' : 'Fenty Beauty',
        category: getCategory(product),
        id:
          typeof otherData.sku !== 'undefined'
            ? otherData.sku
            : product.variants
            ? product.variants[0].sku
            : undefined,
        isBundle:
          typeof product.isBundle !== 'undefined' ? product.isBundle : false,
        name: getProductTitle(product),
        price: Number(formatPriceString(price) / 100),
        productCollection: productType || product.categories[0] || undefined,
        productContext: productContext ? productContext.join('|') : undefined,
        productMasterId: productMasterId || undefined,
        productType,
        quantity,
        variant: otherData.variant
          ? otherData.variant
          : getValueForTag('Color ID', product.tags),
        avg_stars:
          product.reviewsAverage || product.metafields?.yotpo?.reviews_average,
      },
    ],
  };

  if (hasTag('donation', product.tags)) {
    eventData.isDonation = true;
  } else {
    eventData.isDonation = false;
  }

  return eventData;
}

export function getSingleCartItemData(single_item) {
  return {
    ...(!!single_item.properties._bundle_uid && {
      bundle_id: single_item.properties._bundle,
      bundle_name: single_item.properties._bundle_title,
      bundled: true,
      multiple: false,
      bundle_type: 'bundle builder',
      product_context: single_item.properties._product_context,
    }),
    price: single_item.compare_at_price ? single_item.compare_at_price / 100 : single_item.original_price / 100,
    ...(single_item.discounted_price && {
      original_price: single_item.discounted_price / 100,
      discount: single_item.compare_at_price ? Number(((single_item.compare_at_price - single_item.discounted_price) / 100).toFixed(2)) : Number(((single_item.original_price - single_item.discounted_price) / 100).toFixed(2))
    }),
    subscription: single_item.properties?._subscription_frequency || false,
    item_list_id: single_item.properties?._item_list_id || undefined,
    item_list_name: htmlDecode(single_item.properties?._item_list_name) || undefined,
    index: parseInt(single_item.properties?._index),
    origin: single_item.properties?._origin || undefined,
    origin_product: single_item.properties?._origin_product || undefined,
    avg_stars: single_item.properties?._product_rating || undefined,
    product_stock: single_item.properties?._product_available
      ? 'in stock'
      : 'out of stock',
    gift: false,
    product_stock: 'in stock',
    product_context: single_item.properties._product_context
  };
}

export async function trackAddToCart(data) {
  const { product, quantity = 1, isCartItem = false, otherData } = data;
  const price = otherData.price ? otherData.price : Number(formatPriceString(product.price / 100));

  let items = [];
  if (product.isBundle) {
    items = await Promise.all(
      product.items.map(async (item) => {
        item.isBundle = true;
        const data = buildItemTrackingData(item, quantity, otherData);
        if (isCartItem) {
          const itemData = getSingleCartItemData(item);
          return { ...data, ...itemData };
        }

        return { ...data };
      }),
    );
  } else {
    const trackingData = buildItemTrackingData(product, quantity, otherData);
    if (isCartItem) {
      const itemData = getSingleCartItemData(product);
      items = [{ ...trackingData, ...itemData }];
    } else {
      items = [{ ...trackingData }]
    }
  }

  const eventData = {
    event: 'add_to_cart',
    ...(otherData.siteSection && {
      site_section: otherData.siteSection,
    }),
    ...(otherData.componentName && {
      component_name: otherData.componentName,
    }),
    ...(otherData.bundleType && {
      bundle_type: otherData.bundleType,
    }),
    ...(otherData.moduleName && {
      shopify_module_name: otherData.moduleName,
    }),
    ecommerce: {
      currency: window.Shopify.currency.active,
      value: Number(price * +quantity),
      items,
      ...(product.isBundle && {
        bundle_name: product.properties?._bundle_title,
        bundle_id: product.properties?._bundle,
      }),
    },
  };
  trackEvent(eventData);
}

export async function trackRemoveFromCart(data) {
  const { product, quantity = 1 } = data;
  const price = Number(formatPriceString(product.price / 100));
  let items;
  if (product.isBundle) {
    items = await Promise.all(
      product.items.map(async (item) => {
        item.isBundle = true;
        const data = buildItemTrackingData(
          {
            ...item,
            type: item.product_type || '',
            reviewsAverage: item.properties._product_rating,
            available: true,
          },
          product.quantity,
          {},
        );
        const itemData = getSingleCartItemData(item);
        return {
          ...data,
          ...itemData,
        };
      }),
    );
  } else {
    const itemObject = buildItemTrackingData(product, quantity, {});
    const itemData = getSingleCartItemData(product);
    items = [{ ...itemObject, ...itemData }];
  }

  const param = {
    event: 'remove_from_cart',
    ecommerce: {
      currency: window.Shopify.currency.active,
      value: price * +quantity,
      items: items,
    },
  };

  if (product.isBundle && product.properties?._bundle_builder == 'true') {
    param.site_section = window.__GLOBAL__.siteSection;
    param.component_name = 'Mini cart';
    param.ecommerce.bundle_type = 'bundle builder';
    param.ecommerce.bundle_name =
      'bundle builder: ' + product.properties?._bundle_title;
    param.ecommerce.bundle_id = product.properties?._bundle;
  }

  trackEvent(param);
}

export async function listProductsEventData(productsData, otherData) {
  let hasBundleBuilder = false;

  const myProducts = await Promise.all(
    productsData.map(async (product, index) => {
      let tempData = {
        ...otherData,
        index: product.index ? product.index + 1 : index + 1,
        ...(product.listId && { listId: product.listId }),
        ...(product.listName && { listName: product.listName }),
      };
      if (product.isBundle) {
        tempData = {
          ...tempData,
          bundleName: product.title,
          bundleId: product.id,
        };
      }

      const itemObject = buildItemTrackingData(product, 1, tempData);
      if (product.type == 'Bundle Builder') {
        itemObject.bundled = true;
        itemObject.bundle_name = htmlDecode(product.title);
        itemObject.bundle_id = Number(product.id);
        hasBundleBuilder = true;
      } else if (otherData.isBundleBuilder) {
        itemObject.bundled = true;
        itemObject.bundle_name = otherData.bundleName;
        itemObject.bundle_id = Number(otherData.bundleId);
      }
      return itemObject;
    })
  );

  let products = myProducts.reduce((arr, p) => {
    if (Array.isArray(p)) {
      arr = [...arr, ...p];
    } else {
      arr.push(p);
    }
    return arr;
  }, []);

  if (hasBundleBuilder) {
    products = products.map((product) => {
      if (product.item_category == 'Bundle Builder') {
        const {
          item_name,
          item_id,
          price,
          currency,
          index,
          quantity,
          bundled,
          bundle_name,
          bundle_id,
        } = product;
        return {
          item_name,
          item_id,
          price,
          currency,
          index,
          quantity,
          bundled,
          bundle_name,
          bundle_id,
          multiple: false
        }
      } else {
        return product;
      }
    });
  }

  const eventData = {
    listName: otherData.listName,
    listId: otherData.listId,
    products: products,
    hasBundleBuilder,
  };

  return eventData;
}

export function trackProductClick(event, itemObject, otherData) {
  const param = {
    event,
    ...(otherData.siteSection && {
      site_section: otherData.siteSection,
    }),
    ...(otherData.componentName && {
      component_name: otherData.componentName,
    }),
    ...(otherData.moduleName && {
      shopify_module_name: otherData.moduleName,
    }),
    ecommerce: {
      item_list_id: otherData.listId || undefined,
      item_list_name: otherData.listName,
      ...(otherData.origin && { origin: otherData.origin }),
      ...(otherData.navigationCategory && {
        navigation_category: otherData.navigationCategory,
      }),
      ...(otherData.section && { section: otherData.section }),
      items: Array.isArray(itemObject) ? itemObject : [itemObject],
    },
  };
  if (otherData.click_text) {
    param.ecommerce.click_text = otherData.click_text;
  }
  if (otherData.bundleName) {
    param.ecommerce.bundle_name = htmlDecode(otherData.bundleName);
  }
  if (otherData.bundleId) {
    param.ecommerce.bundle_id = String(otherData.bundleId);
  }
  if (otherData.navigationCategory) {
    param.ecommerce.navigation_category = otherData.navigationCategory;
  }
  if (otherData.section) {
    param.ecommerce.section = otherData.section;
  }
  trackEvent(param);
}

export function getSwatchChangeProductData(product) {
  let productType = 'Regular';
  let productContext = '';
  let productMasterId;
  let variant = '';

  if (product.tags) {
    if (hasTag('sample', product.tags)) {
      productType = 'Sample';
    } else if (hasTag('gift', product.tags)) {
      productType = 'Gift';
    }

    if (hasTag('bundle', product.tags)) {
      product.isBundle = true;
    }

    productContext = getAllValueForTag('badge', product.tags);
    productMasterId = getValueForTag('master-id', product.tags);
    variant = getValueForTag('Color ID', product.tags);
  }
  const price = Number(formatPriceString(product.price / 100));

  return {
    products: [
      {
        brand: product.vendor === 'Fenty Skin' ? 'Fenty Skin' : 'Fenty Beauty',
        category: getCategory(product),
        id:
          typeof product.sku !== 'undefined'
            ? product.sku
            : product.variants
            ? product.variants[0].sku
            : '',
        isBundle:
          typeof product.isBundle !== 'undefined' ? product.isBundle : false,
        name: getProductTitle(product),
        price,
        productCollection: product.type ? product.type : undefined,
        productContext: productContext ? productContext.join('|') : undefined,
        productMasterId: productMasterId || undefined,
        productStock: product.available ? 'In stock' : 'Out of stock',
        productType,
        variant,
      },
    ],
  };
}

// View Order Detail
export function pushDataOrderDetail(url) {
  const param = {
    event: 'checkStatus',
    eventCallback: () => {
      document.location = url;
    },
  };
  trackEvent(param);
}

// Internal Banners clicks growth
export function pushDataInternalBanner(url, promoName) {
  const param = {
    event: 'internalBanners',
    promoName,
    eventCallback: () => {
      document.location = url;
    },
  };
  trackEvent(param);
}

// Newsletter subscription rate
export function pushDataSubscription(type, location = '', moduleName) {
  let pageTitle = '';
  const splitSurfix = 'page.';
  if (__GLOBAL__.template.includes(splitSurfix)) {
    pageTitle =
      __GLOBAL__.template.split(splitSurfix)[1].replaceAll('-', ' ') + ' page';
  } else {
    pageTitle = __GLOBAL__.template;
  }

  const param = {
    event: 'newsletter_subscription',
    method: type,
    location: location || pageTitle,
    shopify_module_name: moduleName
  };

  trackEvent(param);
}

// Filter interaction rate
export function pushDataCollectionFilter(filters) {
  const dataLayerObj = [];
  const keys = Object.keys(filters);
  dataLayerObj['filterChoice'] = keys
    .reduce((choices, key) => {
      if (Array.isArray(filters[key])) {
        if (filters[key].length) {
          choices.push(filters[key].join());
        }
      } else if (filters[key]) {
        choices.push(filters[key]);
      }
      return choices;
    }, [])
    .join();
  dataLayerObj['filterOption'] = keys
    .reduce((options, key) => {
      if (Array.isArray(filters[key])) {
        if (filters[key].length) {
          options.push(key);
        }
      } else if (filters[key]) {
        options.push(key);
      }
      return options;
    }, [])
    .join();
  dataLayerObj['filterInteractionType'] = formatString(
    __GLOBAL__.collection.title,
  );

  const param = {
    event: 'filterBy',
    filterChoice: dataLayerObj['filterChoice'],
    filterInteractionType: dataLayerObj['filterInteractionType'],
    filterOption: dataLayerObj['filterOption'],
  };
  trackEvent(param);
}

// Social interactions rate
export function pushDataSocial(brand, social) {
  const param = {
    event: 'socialInteraction',
    brand_environment: brand,
    socialNetwork: social,
  };
  trackEvent(param);
}

// Product page interaction
export function pushDataPdpSpecifications({ productTitle, listTitle }) {
  const productName = productTitle.split(__GLOBAL__.productDelimiter)[0].trim();

  const title = formatString(listTitle.split('\n')[0]);

  const param = {
    event: 'productFeature',
    interactionType: title,
    productName,
  };
  trackEvent(param);
}

// Shade finder page interaction
export function shadeFinderInteraction(itemInteractionType, productName) {
  const param = {
    event: 'finderInteraction',
    itemInteractionType: formatString(itemInteractionType),
    productName,
  };
  trackEvent(param);
}

export function trackFindMyShade({ action, shopify_module_name }) {
  const param = {
    event: action,
    shopify_module_name: shopify_module_name
  };
  trackEvent(param);
}

export function trackTryShadeOn({ action, shopify_module_name }) {
  const param = {
    event: action,
    shopify_module_name: shopify_module_name
  };
  trackEvent(param);
}

// Finders steps and choices
export function pushDataFinderChoices(config) {
  const param = {
    event: 'finderInteraction',
    finderChoice: config['finderChoice'],
    finderName: 'Shade Finder',
    finderStepName: config['finderStepName'],
  };
  trackEvent(param);
}

// Account sign up conversion rate
export function pushDataSignup(param) {
  trackEvent(param);
}

// Account login growth rate
export function pushDataLogin(param) {
  trackEvent(param);
}

// Finder save growth rate
export function pushDataFinderSaved() {
  const param = {
    event: 'finderSave',
    finderName: 'Shade Finder',
  };
  trackEvent(param);
}

// Notify me button
export function trackGetNotified(productId) {
  const param = {
    event: 'getNotified',
    productId: productId,
  };
  trackEvent(param);
}

function sanitizeData(data) {
  if (typeof data === "string") {
    return data.replace(/[&<>"'%$@#()+,*/:?;=[\]{}]/g, function (match) {
      const escapeMap = {
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
        "%": "&#37;",
        "$": "&#36;",
        "@": "&#64;",
        "#": "&#35;",
        "+": "&#43;",
        "(": "&#40;",
        ")": "&#41;",
        "*": "&#42;",
        ",": "&#44;",
        "/": "&#47;",
        ":": "&#58;",
        ";": "&#59;",
        "=": "&#61;",
        "?": "&#63;",
        "[": "&#91;",
        "]": "&#93;",
        "{": "&#123;",
        "}": "&#125;",
        "’": "&#8217;",
        "-": "&#45;",
        "_": "&#95;"
      };
      return escapeMap[match];
    });
  } else if (typeof data === "object") {
    for (let key in data) {
      const exceptionKeys = ['item_name', 'bundle_name', 'item_list_name', 'item_category', 'origin_product', 'product_context'];
      if (exceptionKeys.includes(key)) {
        data[key] = data[key];
      } else {
        data[key] = sanitizeData(data[key]);
      }
    }
  }
  return data;
}

function validateEvent(eventObj) {
  const requiredFields = ["event"]; // list out any other required fields
  for (let field of requiredFields) {
    if (!eventObj[field] || typeof eventObj[field] !== "string") {
      console.error(`Missing or invalid ${field} in event`, eventObj);
      return false;
    }
  }
  return true;
}

function decodeURIComponents(data) {
  if (typeof data === 'string') {
    try {
      return decodeURIComponent(data);
    } catch (e) {
      console.error('Failed to decode URI component:', data);
      return data; // Return original data if decoding fails
    }
  } else if (typeof data === 'object') {
    for (let key in data) {
      data[key] = decodeURIComponents(data[key]);
    }
  }
  return data;
}

export function trackEvent(param) {
  const decodedEventObj = decodeURIComponents(param); // Decode URI components
  if (validateEvent(decodedEventObj)) {
    const sanitizedEventObj = sanitizeData(decodedEventObj); // Sanitize special characters
    window.dataLayer.push(sanitizedEventObj);
  }

  if (param.eventCallback) {
    param.eventCallback();
  }
}

export function trackCTA(el, ctaText, otherData) {
  let eventData = {
    event: 'cta_click',
    click_text: ctaText,
  };

  if (otherData?.category) {
    eventData = {
      ...eventData,
      navigation_category: otherData.category,
    };
  }
  if (otherData?.tree) {
    eventData = {
      ...eventData,
      navigation_tree: `${otherData.tree}/${ctaText}`,
    };
  }
  if (otherData?.componentName) {
    eventData = {
      ...eventData,
      component_name: otherData.componentName,
    };
  }
  if (otherData?.siteSection) {
    eventData = {
      ...eventData,
      site_section: otherData.siteSection,
    };
  }
  if (el.tagName.toLowerCase() === 'a' && el.getAttribute('href')) {
    eventData = {
      ...eventData,
      eventCallback: function () {
        window.location = el.getAttribute('href');
      },
      eventTimeout: 2000,
    };
  }
  if (otherData?.moduleName) {
    eventData = {
      ...eventData,
      shopify_module_name: otherData.moduleName
    }
  }

  trackEvent(eventData);
}

export async function trackItemView(product, otherData, origin) {
  const price = Number(formatPriceString(product.price / 100));
  let items = [];
  const ecomData = {
    ...otherData,
    ...getLocalStorageProperties(),
  };

  if (otherData.bundleProducts) {
    items = otherData.bundleProducts.map((bundleProduct) => {
      const itemObject = buildItemTrackingData(
        {
          ...bundleProduct,
          isBundle: true,
        },
        1,
        ecomData,
      );
      return itemObject;
    });
  } else {
    const itemObject = buildItemTrackingData(product, 1, ecomData);
    items = [itemObject];
  }

  const eventData = {
    event: 'view_item',
    ...(otherData.componentName && {
      component_name: otherData.componentName,
    }),
    ecommerce: {
      currency: window.Shopify.currency.active,
      value: price,
      origin,
      items,
    },
  };

  trackEvent(eventData);
}

export function trackProductFindShade(product) {
  const productName = getProductTitle(product);
  const eventData = {
    event: 'productFindShade',
    productName,
  };

  trackEvent(eventData);
}

export function trackTryOn(product) {
  const productName = getProductTitle(product);
  const eventData = {
    event: 'productTryOn',
    productName,
  };

  trackEvent(eventData);
}

export function trackProductInspiration({ interactionType, product, videoId }) {
  const eventData = {
    event: 'productInspiration',
    interactionType: formatString(interactionType),
    productName: product.title ? getProductTitle(product) : null,
    videoId: String(videoId),
  };

  trackEvent(eventData);
}

(function () {
  setTimeout(() => {
    const buttons = document.querySelectorAll('.btn');
    if (!!buttons.length) {
      buttons.forEach((button) => {
        const category = button?.dataset?.category;
        const title = button?.dataset?.title;
        const componentName = button?.dataset?.componentName;
        const siteSection = button?.dataset.siteSection;
        const tree = title && category ? `${title}/${category}` : null;
        const moduleName = button?.dataset?.moduleName;
        const otherData = {
          category,
          tree,
          componentName,
          siteSection,
          moduleName
        };

        button.addEventListener('click', (event) => {
          event.currentTarget.tagName.toLowerCase() === 'a' &&
            event.currentTarget.getAttribute('href') &&
            event.preventDefault();

          if (event.currentTarget.children[0]) {
            const text = event.currentTarget.children[0]?.textContent;

            if (
              text?.includes('add to') ||
              text?.includes('skip to') ||
              text?.toLowerCase().includes('find my shade')
            )
              return;

            trackCTA(event.currentTarget, text, otherData);
          } else
            trackCTA(
              event.currentTarget,
              event.currentTarget.innerText,
              otherData,
            );
        });
      });
    }
  }, 3000);
})();

export function trackBannerView({
  id,
  name,
  location,
  component_name,
  site_section,
  shopify_module_name
}) {
  const eventData = {
    event: 'banner_impression',
    banner_name: name,
    banner_id: id,
    ...(location && { banner_location: location }),
    ...(component_name && { component_name }),
    ...(site_section && { site_section }),
    ...(shopify_module_name && { shopify_module_name }),
  };
  trackEvent(eventData);
}

export function trackBannerClick({ id, name, location }) {
  const eventData = {
    event: 'banner_click',
    banner_name: name,
    banner_id: id,
    banner_location: location,
  };
  trackEvent(eventData);
}

function setupChatTracking(iframe) {
  const MAX_RETRIES = 10;
  let retries = 0;

  const checkButtonAndAddListener = () => {
    const iframeDocument = iframe.contentDocument;
    const button = iframeDocument.querySelector('button');

    if (button) {
      button.addEventListener('click', function () {
        trackEvent({ event: 'customer_care_start_chat' });
      });

      clearInterval(interval);
    } else {
      retries++;
      if (retries >= MAX_RETRIES) {
        clearInterval(interval);
      }
    }
  };

  const interval = setInterval(checkButtonAndAddListener, 100);
}

function handleChatTracking() {
  // Set up a MutationObserver to watch for the addition of the chat iframe
  const observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach(function (node) {
          if (
            node.tagName === 'IFRAME' &&
            node.title === CHAT_IFRAME_TITLE_SELECTOR
          ) {
            setupChatTracking(node);
            observer.disconnect();
          }
        });
      }
    });
  });

  // Start observing for changes in the DOM
  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

handleChatTracking();
export function trackKlavioAddToCart({ product, variant }) {
  if (!variant) variant = product.variants[0];

  const getVariantPrice = () => {
    if (!variant.price) return '';
    return formatPrice(variant.price, true);
  };

  const getVariantDiscountPrice = () => {
    if (!variant.compareAtPrice) return false;
    return formatPrice(variant.compareAtPrice, true);
  };

  const variantPrice = getVariantPrice();
  const variantDiscountPrice = getVariantDiscountPrice();
  const variantImage =
    variant.image && !variant.image.src.includes('no-image')
      ? variant.image.src
      : product.featuredImage;

  if (typeof window.klaviyo === 'undefined') {
    return;
  }

  window.klaviyo.push([
    'track',
    'Added to Cart - Shopify',
    {
      Name: product.title,
      ProductID: product.id,
      Categories: product.categories,
      ImageURL: variantImage,
      URL: product.url
        ? `${__GLOBAL__.secureURL}${product.url}`
        : `${__GLOBAL__.secureURL}/products/${product.handle}`,
      Brand: product.vendor,
      Price: variantPrice,
      CompareAtPrice: variantDiscountPrice ? variantDiscountPrice : '$0.00',
    },
  ]);
}

// pw reset
const addPasswordResetButtonTracking = () => {
  const MAX_RETRIES = 6;
  let retries = 0;

  const checkButtonAndAddListener = () => {
    const button = document.querySelector('.account-form--reset button');

    if (button) {
      button.addEventListener('click', () => {
        trackEvent({ event: 'password_reset' });
      });

      clearInterval(interval);
    } else {
      retries++;
      if (retries >= MAX_RETRIES) {
        clearInterval(interval);
      }
    }
  };

  const interval = setInterval(checkButtonAndAddListener, 500);
};

addPasswordResetButtonTracking();

/**
 * Formats product data for areas in the tracking document marked as {{EEC ITEM OBJECT}}.
 * Formats a single product.
 *
 * @param {Object} productObject - The product object containing product data.
 * @param {number} quantity - The quantity of the product.
 * @param {Object} otherData - Additional data to be included in the payload.
 * @returns {Promise<Object>} The formatted payload for tracking.
 */
export function buildItemTrackingData(productObject, quantity, otherData) {
  const hasSubscription = Object.hasOwn(otherData, 'subscriptionPrice');
  let price = otherData.hasOwnProperty('subscriptionPrice')
    ? otherData.subscriptionPrice
    : productObject.price !== undefined
    ? productObject.price
    : productObject.original_price !== undefined
    ? productObject.original_price
    : productObject.variants &&
      productObject.variants.length > 0 &&
      productObject.variants[0].price !== undefined
    ? productObject.variants[0].price
    : 0;

  price = Number(formatPriceString(price / 100));
  let payload = {
    item_name: getProductTitle(productObject),
    item_id:
      productObject.sku !== undefined
        ? productObject.sku
        : productObject.variants
        ? productObject.variants[0]?.sku || undefined
        : undefined,
    affiliation: productObject?.vendor || undefined,
    item_brand:
      productObject.vendor === 'Fenty Skin' ? 'Fenty Skin' : 'Fenty Beauty',
    item_category: getCategory(productObject),
    currency: window.Shopify?.currency?.active || undefined,
    price: Number(price),
    item_variant:
      productObject?.variants?.length > 0
        ? String(productObject?.variants[0]?.id) || ''
        : String(productObject.variant_id),
    quantity,
    product_stock: productObject.available ? 'in stock' : 'out of stock',
    bundled: productObject?.isBundle || false,
    avg_stars:
      productObject?.reviewsAverage || // Liquid sourced
      productObject?.metafields?.yotpo?.reviews_average ||
      undefined, // GraphQL sourced
    subscription: hasSubscription,
    gift: false,
    multiple: false,
  };

  if (Array.isArray(productObject.tags)) {
    const productContext = getAllValueForTag('badge', productObject.tags);
    payload.product_context = productContext
      ? productContext.join('|')
      : undefined;
  }

  if (
    productObject.compareAtPrice ||
    productObject.discount ||
    productObject.variants?.[0]?.compare_at_price ||
    productObject.variants?.[0]?.compareAtPrice
  ) {
    const comparePrice = productObject.compareAtPrice
      ? productObject.compareAtPrice
      : productObject.discount
      ? productObject.discount
      : productObject.variants?.[0]?.compare_at_price
      ? productObject.variants?.[0]?.compare_at_price
      : productObject.variants?.[0]?.compareAtPrice;

    payload.original_price = Number(formatPriceString(comparePrice / 100)); // Convert to dollars
    payload.discount = Number(
      (payload.original_price - payload.price).toFixed(2),
    );
  }

  if (otherData.subFrequency) {
    payload.subscription = otherData.subFrequency;
  }

  // bundle builder
  if (productObject.type == 'Bundle Builder') {
    payload.bundled = true;
    payload.bundle_name = htmlDecode(productObject.title);
    payload.bundle_id = String(productObject.id);
  } else if (productObject?.variants?.length > 0) {
    const variant = productObject.variants[0];
    payload.price = variant.compare_at_price
      ? Number(variant.compare_at_price / 100)
      : variant.compareAtPrice
      ? Number(variant.compareAtPrice / 100)
      : Number(variant.price / 100);
  }

  // swatch variants data
  if (otherData.variant) {
    if (otherData.variant.sku) {
      payload.item_id = otherData.variant.sku;
    }
    if (otherData.variant.id) {
      payload.item_variant = String(otherData.variant.id);
    }
    if (otherData.variant.price) {
      payload.price = parseInt(otherData.variant.price, 10) / 100;
    }
  
    if (otherData.variant.compare_at_price) {
      const compareAtPrice = otherData.variant.compare_at_price
        ? otherData.variant.compare_at_price
        : otherData.variant.compareAtPrice
        ? otherData.variant.compareAtPrice
        : -1;
  
      if (compareAtPrice > -1) {
        payload.original_price = Number(
          formatPriceString(compareAtPrice / 100),
        ); // Convert to dollars
        payload.discount = Number(
          (payload.original_price - payload.price).toFixed(2),
        );
        payload.price = Number(
          formatPriceString(compareAtPrice / 100)
        );
      }
    }
  }

  payload = addOriginData(payload, otherData);
  payload = addListTrackingData(payload, otherData);
  payload = addPromoTrackingData(payload, otherData);
  payload = addCheckoutTrackingData(payload, otherData);
  payload = addBundleTrackingData(payload, otherData);

  if (otherData.bundleType == 'bundle builder') {
    payload.discount = Number((payload.price * (otherData.discount / 100)).toFixed(2));
    if (productObject.compare_at_price && productObject.compare_at_price != productObject.price) {
      payload.discount += (Number(productObject.compare_at_price) - Number(productObject.price)) / 100;
    }
  }

  return payload;
}

/**
 * Adds promotion data to the payload if available.
 *
 * @param {Object} payload - The existing payload object.
 * @param {Object} promotionData - The promotion data containing promotionId and promotionName.
 * @returns {Object} The updated payload with promotion data if available.
 */
function addPromoTrackingData(payload, { promotionId, promotionName }) {
  if (!promotionId && !promotionName) return payload;
  if (payload.promotion_id) payload.promotion_id = promotionId;
  if (payload.promotion_name) payload.promotion_name = promotionName;

  return payload;
}

/**
 * Adds list tracking data to the payload if available.
 *
 * @param {Object} payload - The existing payload object.
 * @param {Object} listData - The list data containing listId, listName, and index.
 * @returns {Object} The updated payload with list tracking data if available.
 */
function addListTrackingData(payload, { listId, listName, index }) {
  if (listId) payload.item_list_id = listId;
  if (listName) payload.item_list_name = htmlDecode(listName);
  if (index) payload.index = index;

  return payload;
}

/**
 * Adds checkout tracking data to the payload if available.
 *
 * @param {Object} payload - The existing payload object.
 * @param {Object} checkoutData - The checkout data containing coupon, discount, and locationId.
 * @returns {Object} The updated payload with checkout tracking data if available.
 */
function addCheckoutTrackingData(payload, { coupon, discount, locationId, bundleType }) {
  if (!coupon && !discount && !locationId) return payload;
  if (coupon) payload.coupon = coupon;
  if (discount) payload.discount = discount;
  if (locationId) payload.location_id = locationId;

  return payload;
}

/**
 * Adds origin data to the payload if available.
 *
 * @param {Object} payload - The existing payload object.
 * @param {Object} originData - The origin data containing origin and originProduct.
 * @returns {Object} The updated payload with origin data if available.
 */
function addOriginData(payload, { origin, originProduct }) {
  if (!origin && !originProduct) return payload;
  if (origin) payload.origin = origin;
  if (originProduct) payload.origin_product = htmlDecode(originProduct);
  return payload;
}

/**
 * Adds bundle data to the payload if available.
 *
 * @param {Object} payload - The existing payload object.
 * @param {Object} bundleData - The bundle data containing bundle Name and Id.
 * @returns {Object} The updated payload with bundle data if available.
 */
function addBundleTrackingData(
  payload,
  { bundleName, bundleId, bundleType, multiple },
) {
  if (!bundleName && !bundleId) return payload;
  if (bundleName) payload.bundle_name = htmlDecode(bundleName);
  if (bundleId) payload.bundle_id = String(bundleId);
  if (bundleType) payload.bundle_type = String(bundleType);
  if (multiple) payload.multiple = multiple;

  return payload;
}

// mega menu
document
  .querySelectorAll('.header-nav__item .header-nav__item-link')
  .forEach((element) => {
    element.addEventListener('click', (event) => {
      event.stopPropagation();
      let text = event.currentTarget
        .querySelector('span')
        .textContent?.trim()
        .toLowerCase();

      const isAnimatedItem = event.currentTarget
        .querySelector('span')
        .querySelectorAll('span').length;
      if (!!isAnimatedItem) {
        text = event.currentTarget
          .querySelector('span')
          .querySelector('span:not(.header-nav__decor-animate)')
          .textContent?.trim()
          .toLowerCase();
      }

      const data = {
        event: 'navigation_header_click',
        click_text: text,
        navigation_category: text,
        navigation_tree: `navigation/${text}`,
      };
      trackEvent(data);
    });
  });

document.querySelectorAll('.header-nav__sublink').forEach((sub) => {
  sub.addEventListener('click', (event) => {
    event.stopPropagation();
    const text = event.currentTarget
      .querySelector('span')
      .textContent?.trim()
      .toLowerCase();
    const category = event.currentTarget.dataset.parent;
    const data = {
      event: 'navigation_header_click',
      click_text: text,
      navigation_category: category,
      navigation_tree: `navigation/${category}/${text}`,
    };
    trackEvent(data);
  });
});
// end mega menu

// footer
setTimeout(() => {
  document.querySelectorAll('.footer__menu a').forEach((element) => {
    element.addEventListener('click', (event) => {
      event.preventDefault();
      const data = {
        event: 'navigation_footer_click',
        click_text: event.currentTarget.text?.trim().toLowerCase(),
      };

      const eligibleItems = [
        'order status',
        'shipping info',
        'returns',
        'contact us',
        'help & faqs',
      ];

      if (eligibleItems.includes(element.innerText.toLowerCase())) {
        trackEvent({
          event: 'help_center_click',
          click_text: event.target.innerText?.trim().toLowerCase(),
        });
      }

      trackEvent(data);
      window.location.href = element.getAttribute('href');
    });
  });

  document.querySelectorAll('.footer__secondary-menu a').forEach((element) => {
    element.addEventListener('click', (event) => {
      event.preventDefault();
      const data = {
        event: 'navigation_footer_click',
        click_text: event.currentTarget.text?.trim().toLowerCase(),
      };
      const helpCenterData = {
        event: 'help_center_click',
        click_text: event.currentTarget.text?.trim().toLowerCase(),
      };

      trackEvent(data);
      trackEvent(helpCenterData);
      window.location.href = element.getAttribute('href');
    });
  });
}, 1200);
// end footer

// checkout discount
const setupCheckoutDiscountTracking = (context) => {
  const MAX_RETRIES = 16;
  let retries = 0;

  // prior to a discount code being applied, the .tags-list element doesn't exist
  // this will search for it, and if found then it means a discount code has been applied
  const findDiscountCodeContainer = () => {
    const discountCodeContainer = context.querySelector('.tags-list');

    if (discountCodeContainer) {
      // get the discount code that was applied
      const code = discountCodeContainer?.querySelector(
        '.reduction-code__text',
      );

      window.dataLayer.push({
        event: 'discount_apply',
        coupon: code.textContent,
      });

      clearInterval(interval);
    } else {
      retries++;
      if (retries >= MAX_RETRIES) {
        clearInterval(interval);
      }
    }
  };

  const interval = setInterval(findDiscountCodeContainer, 500);
};

const findCheckoutDiscountBtn = () => {
  const MAX_RETRIES = 8;
  let retries = 0;

  const checkButtonAndAddListener = () => {
    const discountBtn = document.querySelector(
      'aside.sidebar #checkout_submit',
    );

    if (discountBtn) {
      const discountForm = discountBtn.closest('form');

      discountForm?.addEventListener('submit', () => {
        setupCheckoutDiscountTracking(document.querySelector('aside.sidebar'));
      });

      clearInterval(interval);
    } else {
      retries++;
      if (retries >= MAX_RETRIES) {
        clearInterval(interval);
      }
    }
  };

  const interval = setInterval(checkButtonAndAddListener, 250);
};

findCheckoutDiscountBtn();
// end checkout discount

// pages menu
setTimeout(() => {
  document.querySelectorAll('.pages-menu a').forEach((element) => {
    element.addEventListener('click', (event) => {
      event.preventDefault();
      const helpCenterData = {
        event: 'help_center_click',
        click_text: event.currentTarget.text,
      };

      trackEvent(helpCenterData);
      window.location.href = element.getAttribute('href');
    });
  });
}, 1200);
// end pages menu

/**
 * Formatting object from Search Spring to product for Events
 * @param item
 * @returns {{price: *, vendor: (string[]|number|string|*), variants: (any|*[]), title: *, type: *, tags: *[]}}
 */
const formatSSProductData = (item) => {
  const { price, msrp, rating, uid } = item.mappings?.core || {
    price: 0,
    msrp: 0,
    rating: '0',
    uid: '',
  };
  let payload = {
    title: item.attributes?.ss_title,
    isBundle: !!item.attributes?.mfield_accentuate_bundled_products,
    available: item.attributes?.ss_available === '1',
    type: item.attributes?.product_type,
    price: price * 100,
    compareAtPrice: msrp * 100,
    tags: item.attributes?.tags ? [...item.attributes?.tags] : [],
    vendor: item.attributes?.vendor,
    reviewsAverage: rating ? rating : '0',
    variants: item.attributes?.variants
      ? JSON.parse(item.attributes.variants)
      : [],
    id: uid,
    ...(item.attributes?.mfield_accentuate_bundled_products && {
      metafields: {
        accentuate: {
          bundled_products: JSON.parse(
            item.attributes?.mfield_accentuate_bundled_products,
          ),
        },
      },
    }),
  };
  return payload;
};

let trackedElements = new Set();
let collectionHandle = null;
function addPLPSortTracking() {
  window.addEventListener('searchspring.domReady', (e) => {
    const items = [...window.searchspring.controller.search.store.results].map(
      (item, index) => ({ ...formatSSProductData(item), index: index }),
    );
    const productCardElements = document.querySelectorAll('.ss__result--item');
    if (e.target?.collection?.handle != collectionHandle) {
      collectionHandle = e.target?.collection?.handle;
      trackedElements = new Set();
    }
    let tempQueue = [];

    const handleTracking = async (queue) => {
      const { products, hasBundleBuilder } = await listProductsEventData(
        queue,
        {
          origin:
            window.searchspring.controller?.search?.context?.collection
              ?.handle || 'search',
          originProduct:
            window.searchspring.controller?.search?.context?.collection?.name ||
            'Search',
        },
      );

      let ecommerceData = {
        item_list_id:
          window.searchspring.controller?.search?.context?.collection?.handle ||
          'search',
        item_list_name:
          window.searchspring.controller?.search?.context?.collection?.name ||
          'Search',
        items: products,
      };

      if (hasBundleBuilder) {
        ecommerceData = {
          ...ecommerceData,
          bundled: true,
        };
      }

      let eventData = {
        event: 'view_item_list',
        ecommerce: ecommerceData,
      };
      if (hasBundleBuilder) {
        eventData = {
          ...eventData,
          site_section: window.__GLOBAL__.siteSection,
          component_name: 'Bundle Builder Product Card',
          bundle_type: 'bundle builder',
        };
      }
      trackEvent(eventData);
    };

    const observerCallback = (target, isIntersection, unobserve) => {
      if (isIntersection && !trackedElements.has(target)) {
        const index = [...productCardElements].indexOf(target);
        const product = items[index];
        tempQueue.push(product);
        trackedElements.add(target);
        unobserve();

        // On mobile, there are 2 products per row
        if (window.innerWidth < 992 && tempQueue.length === 2) {
          handleTracking(tempQueue);
          tempQueue = [];
        } else if (window.innerWidth >= 992 && tempQueue.length === 4) {
          handleTracking(tempQueue);
          tempQueue = [];
        } else {
          // Edge case: last row isn't full
          if (items.length === trackedElements.size) {
            handleTracking(tempQueue);
            tempQueue = [];
          }
        }
      }
    };
    const observer = obzerv.create({
      callback: observerCallback,
      offset: '0px',
    });

    productCardElements.forEach((productCard) => {
      observer.track(productCard);
    });

    const sortOption = document.querySelector(
      '.ss__dropdown .sort-by .currentOption',
    );
    if (sortOption) {
      if (window.currentSort && window.currentSort !== sortOption.textContent) {
        trackEvent({
          event: 'product_sort',
          sort_by: sortOption.textContent,
        });
      } else {
        window.currentSort = sortOption.textContent;
      }
    }
  });
}

addPLPSortTracking();

// Fire the code snippet code below when the user clicks on a specific service to explore or to use
export function trackExclusiveServicesClick(
  url,
  click_text = undefined,
  section = undefined,
  callback = undefined,
) {
  const param = {
    event: 'four_up_click',
    section,
    click_text,
    eventCallback: () => {
      callback ? callback() : (document.location = url);
    },
    eventTimeout: 2000,
  };

  trackEvent(param);
}

// Fire the code snippet below when the user views the banner:
export function trackPromoBannerView(el) {
  if (!el) return;

  const options = {
    root: null,
    threshold: 0.5,
  };

  const trackingData = {
    banner_name: el.dataset.name,
    banner_id: el.dataset.id,
    banner_location:
      el.dataset.template === 'index'
        ? 'Home page'
        : `${el.dataset.template} page`,
  };

  const callback = function (entries, observer) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const isPromoBarViewTracked = window.dataLayer.filter(
          (item) =>
            item.event === 'banner_impression' &&
            item.banner_id === el.dataset.id,
        );

        if (!isPromoBarViewTracked.length) {
          window.dataLayer.push({
            event: 'banner_impression',
            ...trackingData,
            shopify_module_name: 'promo banner'
          });

          trackPromoBannerClick(el, trackingData);
        }
      }
    });
  };

  const observer = new IntersectionObserver(callback, options);
  observer.observe(el);
}

// Fire the code snippet below when the user clicks on the banner:
function trackPromoBannerClick(target, trackingData) {
  target.addEventListener(
    'click',
    () => {
      window.dataLayer.push({
        event: 'banner_click',
        ...trackingData,
        shopify_module_name: 'promo banner'
      });
    },
    { once: true },
  );
}

// Fire the code snippet below when the user click Notify Me
export function trackNotifyMeBtnClicked(productTitle, moduleName, shopifyModuleName) {
  dataLayer.push({
    event: 'product_module_interaction',
    module_name: htmlDecode(moduleName),
    click_action: 'notify_me_click',
    product_name: htmlDecode(productTitle),
    shopify_module_name: shopifyModuleName
  });
}
